// File: src/components/Charts/MultiLineChart.jsx

import React from 'react';
import {
  ResponsiveContainer,
  LineChart as RechartLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

// Helper to format 'YYYYMMDD' → 'MMM D' (e.g. 'Jul 30')
function formatDateString(dateStr) {
  if (typeof dateStr !== 'string' || dateStr.length !== 8) return dateStr;
  const year = Number(dateStr.slice(0, 4));
  const month = Number(dateStr.slice(4, 6)) - 1;
  const day = Number(dateStr.slice(6, 8));
  return new Date(year, month, day)
    .toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Props:
 * - data: array of { date: 'YYYYMMDD', [channelName]: number, … }
 * - lines: [{ dataKey: 'Direct', name: 'Direct' }, …]
 * - height (optional): chart height in px (default 300)
 */
export default function MultiLineChart({ data, lines, height = 300 }) {
  // colors for up to 5 lines
  const colors = ['#6A0DAD', '#00A8A9', '#ffc658', '#ff7300', '#8dd1e1'];
  
  // Build a sorted, zero-filled copy of the data
  // 1) First, collect all unique channels from lines
  const channelKeys = lines.map(l => l.dataKey);

  // 2) Sort input data by date
  const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));

  // 3) Zero-fill missing
  const filled = sorted.map(entry => {
    const e = { ...entry };
    channelKeys.forEach(ch => {
      if (e[ch] == null) e[ch] = 0;
    });
    return e;
  });

  const hasSinglePoint = filled.length <= 1;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartLineChart
        data={filled}
        margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
      >
        <CartesianGrid stroke="#e0e0e0" strokeDasharray="3 3" />

        <XAxis
          dataKey="date"
          tickFormatter={formatDateString}
          tick={{ fontSize: 12, angle: -30, textAnchor: 'end' }}
          interval={0}
          label={{
            value: 'Date',
            position: 'insideBottom',
            offset: -40,
            fontSize: 12
          }}
        />

        <YAxis
          tick={{ fontSize: 12 }}
          width={50}
          label={{
            value: 'Sessions',
            angle: -90,
            position: 'insideLeft',
            fontSize: 12,
            offset: 10
          }}
        />

        <Tooltip
          labelFormatter={formatDateString}
          formatter={(value, name) => [`${value} sessions`, name]}
        />

        <Legend verticalAlign="top" height={36} />

        {lines.map(({ dataKey, name }, idx) => (
          <Line
            key={dataKey}
            type="monotone"
            dataKey={dataKey}
            name={name}
            dot={hasSinglePoint}
            connectNulls
            stroke={colors[idx % colors.length]}
          />
        ))}
      </RechartLineChart>
    </ResponsiveContainer>
  );
}